import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { BookOpen, Plus, Settings, TrendingUp, Clock, FileText, Users } from "lucide-react";
import { ProjectCardSkeleton, StatsSkeleton } from "@/components/ui/skeleton";
import { Link } from "react-router-dom";
import { useAuth } from "@/hooks/use-auth";
import { DashboardLayout } from "@/components/DashboardLayout";
import { cacheManager } from "@/utils/cacheUtils";
import { apiService } from "@/services/api";
import ProjectCard from "@/components/ProjectCard";

// Import new DTO types from shared schema
import type { 
  ProjectListItemDTO, 
  ChapterSummaryDTO, 
  ProjectProgressDTO,
  ChapterProgressDTO 
} from "../../../shared/schema";

interface Project {
  id: number;
  uuid?: string;
  title: string;
  bookType: string;
  targetWordCount: number;
  currentWordCount: number;
  status: string;
  progress: number; // This will be calculated dynamically now
  updatedAt: string;
  createdAt: string; // Added for sorting
  projectData?: any;
}

interface CreditData {
  balance: number;
}

export default function MyStoriesPage() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  const deleteMutation = useMutation({
    mutationFn: async (project: { id: number; uuid?: string }) => {
      // Use UUID if available, fallback to numeric ID
      const projectIdentifier = project.uuid || project.id;
      return await apiService.delete(`/api/story-projects/${projectIdentifier}`);
    },
    // Optimistic update: remove project from UI immediately
    onMutate: async (deletingProject) => {
      
      // Cancel outgoing refetches (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({ queryKey: ['/api/story-projects/summaries'] });

      // Snapshot the previous value
      const previousProjects = queryClient.getQueryData(['/api/story-projects/summaries']);

      // Optimistically update to remove the project
      queryClient.setQueryData(['/api/story-projects/summaries'], (old: any) => {
        if (!old) return [];
        
        const filtered = old.filter((p: any) => {
          // Remove project if either ID or UUID matches
          const matchesId = p.id === deletingProject.id;
          const matchesUuid = p.uuid && deletingProject.uuid && p.uuid === deletingProject.uuid;
          const shouldRemove = matchesId || matchesUuid;
          
          if (shouldRemove) {
          }
          
          return !shouldRemove;
        });
        
        return filtered;
      });

      // Return a context object with the snapshotted value
      return { previousProjects };
    },
    onSuccess: () => {
      // Clear localStorage cache but don't invalidate queries since optimistic update worked
      cacheManager.invalidateProjectSummaries();
    },
    onError: (error: any, deletingProject, context) => {
      console.error('❌ Delete mutation error:', error);
      
      // Handle 404 as success (project already deleted)
      if (error.message?.includes('404') || error?.response?.status === 404) {
        cacheManager.invalidateProjectSummaries();
        return;
      }
      
      // For other errors, show error message and roll back
      alert('Failed to delete project');
      queryClient.setQueryData(['/api/story-projects/summaries'], context?.previousProjects);
    },
    onSettled: () => {
      // Only invalidate on error to get fresh data, not on success
    }
  });
  useEffect(() => {
  // This code runs ONLY in the browser.
}, []); // The empty array [] means it runs once when the component mounts.

  const handleDelete = async (project: { id: number; uuid?: string }) => {
    if (confirm('Are you sure you want to delete this project? This cannot be undone.')) {
      await deleteMutation.mutateAsync(project);
    }
  };

  // Fetch user's story projects using optimized summary endpoint with caching
  const { data: fetchedProjects, isLoading: projectsLoading } = useQuery<ProjectListItemDTO[]>({
    queryKey: ["/api/story-projects/summaries"],
    queryFn: async () => {
      // Always fetch from API when React Query calls this function
      const data = await apiService.get('/api/story-projects/summaries');

      // Cache the result for offline/initial load scenarios
      cacheManager.setProjectSummaries(data);
      
      return data;
    },
    enabled: !!user,
    staleTime: 30000, // 30 seconds
    gcTime: 300000, // 5 minutes cache
    // Use localStorage cache as initial data (non-blocking)
    initialData: () => {
      const cached = cacheManager.getProjectSummaries();
      if (cached) {
        return cached;
      }
      return undefined;
    },
    // Mark initial data as stale so it refetches in background
    initialDataUpdatedAt: () => {
      return 0; // Always consider initial data as stale
    },
  });

  // Ensure projects is an array and sort it by createdAt in descending order (latest first)
  const projects = fetchedProjects 
    ? [...fetchedProjects].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
    : [];

  // Fetch user's credit balance
  const { data: creditData } = useQuery<CreditData>({
    queryKey: ["/api/credits/stats"],
    enabled: !!user,
  });
  const creditBalance = creditData?.balance || 0;

  // Helper functions removed - now using unified ProjectCard component

  if (!user) {
    return (
      <div className="container mx-auto px-4 py-8 text-center">
        <h1 className="text-2xl font-bold mb-4">Please sign in to access your stories</h1>
        <Link to="/auth">
          <Button>Sign In</Button>
        </Link>
      </div>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 sm:space-y-8">
        {/* Welcome Section */}
        <div className="mb-6 sm:mb-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2">Welcome back, {user.firstName}!</h2>
          <p className="text-slate-600 text-sm sm:text-base">Ready to continue your storytelling journey?</p>
        </div>

        {/* Quick Stats */}
        {projectsLoading ? (
          <StatsSkeleton />
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6 mb-6 sm:mb-8">
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-purple-100 rounded-lg">
                    <BookOpen className="h-6 w-6 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{projects?.length || 0}</p>
                    <p className="text-sm text-slate-600">Total Projects</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-green-100 rounded-lg">
                    <TrendingUp className="h-6 w-6 text-green-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{projects?.filter(p => (p.progress?.overall || 0) > 0 && (p.progress?.overall || 0) < 100)?.length || 0}</p>
                    <p className="text-sm text-slate-600">In Progress</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-blue-100 rounded-lg">
                    <FileText className="h-6 w-6 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{projects?.filter(p => (p.progress?.overall || 0) >= 100)?.length || 0}</p>
                    <p className="text-sm text-slate-600">Completed</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-orange-100 rounded-lg">
                    <Clock className="h-6 w-6 text-orange-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">
                      {projects?.length > 0 ? Math.round(projects.reduce((total, p) => total + (p.progress?.overall || 0), 0) / projects.length) : 0}%
                    </p>
                    <p className="text-sm text-slate-600">Avg Progress</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Actions Section */}
        <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center mb-6 sm:mb-8">
          <h3 className="text-xl sm:text-2xl font-bold text-slate-900">My Story Projects</h3>
          
          <Link to="/story-idea" className="w-full sm:w-auto">
            <Button className="bg-purple-600 hover:bg-purple-700 w-full sm:w-auto h-12 sm:h-auto">
              <Plus className="mr-2 h-4 w-4" />
              New Story Project
            </Button>
          </Link>

        </div>

        {/* Projects Grid */}
        {projectsLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {Array.from({ length: 6 }).map((_, i) => (
              <ProjectCardSkeleton key={i} />
            ))}
          </div>
        ) : projects && projects.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {projects.map((project: any) => (
              <ProjectCard
                key={project.id}
                project={project}
                onDelete={handleDelete}
                isDeleting={deleteMutation.isPending}
                variant="detailed"
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="mb-6">
              <BookOpen className="h-16 w-16 text-slate-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-slate-900 mb-2">No stories yet</h3>
              <p className="text-slate-600">Start your writing journey by creating your first story project.</p>
            </div>
            <Link to="/story-idea">
              <Button className="bg-purple-600 hover:bg-purple-700">
                <Plus className="mr-2 h-4 w-4" />
                Create Your First Story
              </Button>
            </Link>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}